import {ARR, BOOL, NULL, NUM, OBJ, parse, STR} from "partial-json";
import {differenceBetweenDates} from "./FormatDates.js";

export const parsePartialCandidateData = (partialResumeData = "") => {
  if (!partialResumeData) return null;

  if (partialResumeData[0] !== "{") throw new Error("Hmm, this doesn't look like a resume.");

  const data = parse(partialResumeData, STR | OBJ | NUM | ARR | NULL | BOOL);

  if (!data) return null;

  return {
    data: {
      ...conditionalString(data, "first_name", "firstName"),
      ...conditionalString(data, "last_name", "lastName"),
      ...conditionalString(data, "usa_city", "city"),
      ...conditionalString(data, "usa_state", "state"),
      ...conditionalString(data, "country", "country"),
      ...conditionalString(data, "about_me", "about", 750),
      ...conditionalArrayOfStrings(data, "top_three_exceptional_abilities_short", "exceptionalAbility", null, 50),
      ...conditionalArrayOfStringsToObject(data, "top_three_preferably_hard_but_also_soft_skills_short", "skills", "title", {rank: 6}, null, 255),
      ...conditionalArrayOfStringsToObject(data, "accomplishments_awards_and_or_recognitions", "accomplishments", "title", {}, null, 255),
      ...conditionalArrayOfStrings(data, "languages", "languages", ["English"]),
      ...conditionalEducation(data),
      ...conditionalArrayOfStringsToObject(data, "certificates_certifications_and_licenses", "licenses", "title", {path: ""}, null, 255),
      ...conditionalCareerPassport(data),
      ...conditionalLink(data, "linkedin_url", "linkedIn", "linkedin.com"),
      ...conditionalLink(data, "facebook_url", "facebook", "facebook.com"),
      ...conditionalLink(data, "instagram_url", "instagram", "instagram.com"),
      ...conditionalLink(data, "personal_or_portfolio_website_url", "website"),
      ...conditionalNumber(data, "estimated_hourly_wage", "desiredHourlyPay", null, 7.25, 500)
    },
    searchTerms: {
      ...conditionalArrayOfStrings(data, "industries_search_keywords", "industries"),
      ...conditionalArrayOfStrings(data, "job_titles_search_keywords", "jobs"),
    }
  };
};

const conditionalString = (data, key, outputKey, maxLength = 255) => {
  if (!data?.[key] || typeof data[key] !== "string" || data[key] === "null") return {};
  return {[outputKey]: data[key].substring(0, maxLength)};
};

const conditionalNumber = (data, key, outputKey, minDigits = null, min = null, max = null) => {
  if (!data?.[key] || isNaN(data?.[key])) return {};
  if (minDigits && (data[key] + "").length < minDigits) return {[outputKey]: null};
  if (min && data[key] <= min) return {[outputKey]: min};
  if (max && data[key] >= max) return {[outputKey]: max};
  return {[outputKey]: data[key]};
};

const conditionalArrayOfStrings = (data, key, outputKey, defaultValue = null, maxLength = 255) => {
  if (!data?.[key] || !Array.isArray(data[key])) {
    if (data?.[key]) console.error(`${key}: ${data?.[key]}`);
    return defaultValue ? {[outputKey]: defaultValue} : {};
  }

  const values = data[key]
    .filter((entry) => !!entry && entry !== "null" && typeof entry === "string")
    .map((entry) => entry.substring(0, maxLength));

  if (!values || values.length === 0) return defaultValue ? {[outputKey]: defaultValue} : {};
  return {[outputKey]: values};
};

const conditionalArrayOfStringsToObject = (data, key, outputKey, mapStringToProperty, baseObject, defaultValue = null, maxLength = 255) => {
  if (!data?.[key] || !Array.isArray(data[key])) {
    if (data?.[key]) console.error(`${key}: ${data?.[key]}`);
    return defaultValue ? {[outputKey]: defaultValue} : {};
  }

  let values = data[key]
    .filter((entry) => !!entry && entry !== "null" && typeof entry === "string")
    .map((entry) => {
      if (typeof entry !== "string") return {
        ...baseObject,
        [mapStringToProperty]: "",
      };

      return {
        ...baseObject,
        [mapStringToProperty]: entry.substring(0, maxLength),
      };
    });

  if (!values || values.length === 0) return defaultValue ? {[outputKey]: defaultValue} : {};
  return {[outputKey]: values};
};

const conditionalEducation = (data) => {
  if (!data?.education || !Array.isArray(data.education) || data.education.length === 0) return {};

  return {
    education: data.education.map((entry) => {
      const output = {};
      if (entry?.field_of_study) output.primary = entry.field_of_study;
      if (entry?.institution) output.title = entry.institution;
      if (entry?.currently_attending) output.completion = "Currently Attending";
      else if (entry?.completed_slash_graduated) output.completion = "Graduated";
      let validStart = getValidDate(entry?.start);
      if (validStart) output.start = validStart;
      let validEnd = getValidDate(entry?.end);
      if (validEnd) output.end = validEnd;
      const validTypes = ["High School/GED", "Associate's Degree", "Bachelor's Degree", "Master's Degree", "Doctorate's Degree", "Other"];
      if (entry?.type && validTypes.includes(entry.type)) output.type = entry.type;
      else entry.type = "Other";
      if (entry?.description && entry.description !== "null") output.description = entry.description;
      return output;
    }),
  };
};

const conditionalCareerPassport = (data) => {
  if (!data?.experience || !Array.isArray(data.experience) || data.experience.length === 0) return {};

  return {
    careerPassport: data.experience.map((entry) => {
      const output = {};
      if (entry?.title) output.primary = entry.title;
      if (entry?.organization) output.title = entry.organization;
      if (entry?.description && entry.description !== "null") output.description = entry.description;

      let validStart = getValidDate(entry?.start);
      let validEnd = getValidDate(entry?.end);

      if (validStart) output.start = validStart;
      if (validEnd) output.end = validEnd;
      if (entry?.is_currently_working_here) output.completion = "Currently Working Here";
      else if (validEnd) output.completion = "No Longer Working Here";

      if (validStart) {
        try {
          const startDate = new Date(validStart + "T00:00");
          let endDate;

          if (validEnd) endDate = new Date(validEnd + "T00:00");
          else endDate = new Date();

          entry.tenure = differenceBetweenDates(startDate, endDate, "years");
        } catch (e) {
          console.error(e);
        }
      }
      if (entry?.title_search_keywords && Array.isArray(entry.title_search_keywords) && entry.title_search_keywords.length > 0) output.title_keywords = entry.title_search_keywords;
      if (entry?.industry_search_keywords && Array.isArray(entry.industry_search_keywords) && entry.industry_search_keywords.length > 0) output.industry_keywords = entry.industry_search_keywords;
      return output;
    }),
  };
};

const conditionalLink = (data, key, outputKey, requiredHostname = null) => {
  if (!data?.[key]) return {};

  try {
    const url = new URL(data[key]);
    if (!url || !url?.hostname) return {};

    if (requiredHostname && url.hostname !== requiredHostname) return {};

    return {[outputKey]: url.href.replace("www.", "")};
  } catch (e) {
    console.error(e);
    return {};
  }
};

const getValidDate = (partialDate) => {
  if (!partialDate) return null;
  let partialDateString = partialDate + "";
  if (partialDateString.length < 7) return null; // We need at least the month and year, aka 2024-01

  let dateString = "";

  switch (partialDateString.length) {
  case 7:
    dateString = partialDateString + "-01";
    break;
  case 8:
    dateString = partialDateString + "01";
    break;
  case 9:
    if (partialDateString[8] === "0") dateString = partialDateString + "1"; // Edge case, don't autocomplete 2024-12-3 to 31
    else dateString = partialDateString + "0";
    break;
  case 10:
    dateString = partialDateString + "T00:00";
    break;
  }

  try {
    const date = new Date(dateString);
    if (date instanceof Date && !isNaN(date)) return dateString;
  } catch (e) {
    console.error(e);
  }

  return null;
};
